import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Collapse,
  Flex,
  Checkbox,
  message,
  Modal,
  Result,
} from "antd";
import { DeleteOutlined, CheckCircleOutlined } from "@ant-design/icons";
import "./CartPage.scss";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { fetchCart } from "../../../features/cart/cartSlice";
import {
  getLocalStorageData,
  setLocalStorageData,
} from "../../../shared/commonFunction";
import { deleteApiRequest } from "../../../services/deleteApiRequest";
import { END_POINTS } from "../../../Helper/Constant";
import { showToast } from "../../../shared/sharedComponents/ToasterMessage/ToasterMessage";
import Swal from "sweetalert2";
import { putApiRequest } from "../../../services/putApiRequest";
import { hideLoader, showLoader } from "../../../features/loader/loaderSlice";
import { postApiRequest } from "../../../services/postApiRequest";
import PropTypes from "prop-types";
import coin from "../../../assets/icons/header-coin.svg";
import { useNavigate } from "react-router-dom";
import { getApiRequest } from "../../../services/getApiRequest";
import { setUser } from "../../../features/user/userSlice";

const { Text } = Typography;

// Prop types for extracted components
const CartItem = ({
  item,
  isRTL,
  rtlStyles,
  selectedProduct,
  onCheckboxChange,
  onRemoveProduct,
  t,
}) => (
  <div className="cart-item" style={rtlStyles}>
    <Checkbox
      checked={selectedProduct?._id === item._id}
      onChange={(e) => onCheckboxChange(item, e.target.checked)}
      style={{
        marginRight: rtlStyles.marginRight,
        marginLeft: rtlStyles.marginLeft,
      }}
    />
    <div className="cart-info">
      <Flex gap={20} align="center" direction={rtlStyles.flexDirection}>
        <img
          src={item.product?.product_image || ""}
          alt={item.product?.name}
          className="cart-img"
        />
        <div style={rtlStyles}>
          <h4 className="product-name">{item.product?.name}</h4>
          <span>
            {t("cart.price")}:{" "}
            <img src={coin} alt="coin" className="market-place-coin" />{" "}
            {parseInt(item.amountInCoins) / item.quantity}
          </span>
        </div>
      </Flex>
    </div>

    <div className="subtotal">
      {t("cart.subtotal")}:{" "}
      <img src={coin} alt="coin" className="market-place-coin" />{" "}
      {item.amountInCoins?.toLocaleString()}
    </div>

    <DeleteOutlined
      className="delete-btn"
      onClick={() => onRemoveProduct(item)}
      style={{
        marginLeft: isRTL ? "0" : "auto",
        marginRight: isRTL ? "auto" : "0",
      }}
    />
  </div>
);

CartItem.displayName = "CartItem";

CartItem.propTypes = {
  item: PropTypes.shape({
    _id: PropTypes.string,
    quantity: PropTypes.number.isRequired,
    amountInCoins: PropTypes.number.isRequired,
    product: PropTypes.shape({
      name: PropTypes.string,
      product_image: PropTypes.string,
    }),
  }).isRequired,
  isRTL: PropTypes.bool.isRequired,
  rtlStyles: PropTypes.object.isRequired,
  selectedProduct: PropTypes.object,
  onCheckboxChange: PropTypes.func.isRequired,
  onRemoveProduct: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

// Prop types for InsufficientBox component
const InsufficientBox = ({
  neededCoins,
  selectedProduct,
  styles,
  buyButtonStyles,
  onBuyNow,
  t,
}) => (
  <div className="insufficient-box" style={styles}>
    <div className="coin-icon">
      <img src={coin} alt="coin" />
    </div>

    {neededCoins > 0 && (
      <Text
        className="needed-coins"
        style={{ display: "block", textAlign: "center" }}
      >
        <img src={coin} alt="coin" className="market-place-coin" />
        {t("cart.needed_coins").replace("{{count}}", neededCoins) ||
          "Coins Needed"}
      </Text>
    )}

    <p
      className="overview-item-text"
      style={{ textAlign: "center", margin: "10px 0" }}
    >
      {neededCoins > 0
        ? t("cart.insufficient_balance_message")
        : !selectedProduct
          ? t("cart.select_product_message")
          : t("cart.sufficient_balance_message")}
    </p>

    <Button
      className="redeem-btn"
      type="primary"
      onClick={onBuyNow}
      disabled={neededCoins > 0 || !selectedProduct}
      style={buyButtonStyles}
    >
      {t("product.buy_now") || "Buy Now"}
    </Button>
  </div>
);

InsufficientBox.displayName = "InsufficientBox";

InsufficientBox.propTypes = {
  neededCoins: PropTypes.number.isRequired,
  selectedProduct: PropTypes.object,
  styles: PropTypes.object,
  buyButtonStyles: PropTypes.object,
  onBuyNow: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

// Memoized versions of the components
const MemoizedCartItem = React.memo(CartItem);
const MemoizedInsufficientBox = React.memo(InsufficientBox);

const CartPage = () => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, error } = useSelector((state) => state.cart);
  const userData =
    useSelector((state) => state.user.userData) ||
    JSON.parse(getLocalStorageData("user") || "{}");

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(2);
  const [orderDetails, setOrderDetails] = useState(null);
  const isRTL = i18next.language === "ar";

  useEffect(() => {
    console.log("i18next.language: ", i18next.language);
    dispatch(fetchCart(i18next.language));
  }, [dispatch, t]);

  const cartItems = useMemo(() => items?.cartItems || [], [items]);
  console.log('cartItems: ', cartItems);

  // FIXED: Auto-select logic - select first item when cart updates
  useEffect(() => {
    if (cartItems.length > 0) {
      // If no product is selected, select the first one
      if (!selectedProduct) {
        setSelectedProduct(cartItems[0]);
      } else {
        // Check if currently selected product still exists in cart
        const stillExists = cartItems.find(
          (item) => item._id === selectedProduct._id
        );
        if (!stillExists) {
          // If selected product was removed, select the first available item
          setSelectedProduct(cartItems[0]);
        } else {
          // Update the selected product with fresh data from cart
          setSelectedProduct(stillExists);
        }
      }
    } else {
      // Cart is empty, clear selection
      setSelectedProduct(null);
    }
  }, [cartItems]); // Only depend on cartItems, not selectedProduct to avoid loops

  // Handle success modal countdown and redirect
  useEffect(() => {
    let countdownInterval;
    if (showSuccessModal && redirectCountdown > 0) {
      countdownInterval = setInterval(() => {
        setRedirectCountdown((prev) => prev - 1);
      }, 1000);
    } else if (showSuccessModal && redirectCountdown === 0) {
      navigate("/order-details", { state: { order: orderDetails } });
    }

    return () => {
      if (countdownInterval) {
        clearInterval(countdownInterval);
      }
    };
  }, [showSuccessModal, redirectCountdown, navigate, orderDetails]);

  // Reset countdown when modal closes
  useEffect(() => {
    if (!showSuccessModal) {
      setRedirectCountdown(2);
    }
  }, [showSuccessModal]);

  // FIXED: Memoized calculations - properly calculate based on selected product
  const totalCoinsForSelected = useMemo(() => {
    if (!selectedProduct) return 0;
    // Use the amountInCoins which already includes quantity calculation
    return selectedProduct.amountInCoins || 0;
  }, [selectedProduct]);

  const userCoins = useMemo(() => userData?.totalCoins || 0, [userData]);

  const neededCoins = useMemo(
    () =>
      totalCoinsForSelected > userCoins ? totalCoinsForSelected - userCoins : 0,
    [totalCoinsForSelected, userCoins]
  );

  // Memoized handlers
  const handleCheckboxChange = useCallback((item, checked) => {
    setSelectedProduct(checked ? item : null);
  }, []);

  const handleRemoveProduct = useCallback(
    async (item) => {
      const result = await Swal.fire({
        title: t("cart.confirm_delete_title"),
        text: t("cart.confirm_delete_text"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: t("cart.confirm_button_text"),
        cancelButtonText: t("cart.cancel_button_text"),
      });

      if (!result.isConfirmed) return;

      dispatch(showLoader());
      try {
        const response = await deleteApiRequest(
          END_POINTS.REMOVE_ITEM_FROM_CART,
          item._id
        );
        if (response.success) {
          // Clear selection if the removed item was selected
          if (selectedProduct && selectedProduct._id === item._id) {
            setSelectedProduct(null);
          }
          showToast(t(response?.message), "success");
          // Refresh cart - the useEffect will handle auto-selection
          dispatch(fetchCart(i18next.language));
          Swal.fire({
            title: t("cart.deleted_title"),
            text: t("cart.deleted_message"),
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
          });
        }
      } catch (error) {
        console.log("error: ", error);
        showToast(error?.message || "Failed to remove item from cart", "error");
      } finally {
        dispatch(hideLoader());
      }
    },
    [dispatch, selectedProduct, t]
  );

  const handleBuyNow = useCallback(async () => {
    if (!selectedProduct) {
      message.error(t("cart.select_product_error"));
      return;
    }

    if (neededCoins > 0) {
      message.error(t("cart.insufficient_coins"));
      return;
    }

    const result = await Swal.fire({
      title: t("cart.confirm_purchase_title"),
      text: t("cart.confirm_purchase_text"),
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: t("cart.confirm_purchase_button"),
      cancelButtonText: t("cart.cancel_button_text"),
    });

    if (!result.isConfirmed) return;

    dispatch(showLoader());
    try {
      const payload = {
        productId: selectedProduct.product?._id || selectedProduct._id,
        amount: selectedProduct?.amount,
        coins: selectedProduct?.amountInCoins,
        // card_message: "This is the custom message on card.",
        // message: "Well Done! 🎉\nI thought you would like this gift!",
        extra_fields: {
          // department: "Information Technology",
          // customer_id: "A1232",
          // pi_12: "12Ag",
          // is_admin: true,
        },
        delivery_language: i18next.language || "en",
        notify: true,
      };

      const response = await postApiRequest(END_POINTS.PLACE_ORDER, payload);

      if (response.success) {
        showToast(
          t(response?.message) || "Order placed successfully!",
          "success"
        );

        // Refresh cart and clear selection
        dispatch(fetchCart(i18next.language));
        setSelectedProduct(null);

        const data = response?.data;
        const orderPayload = {
          key: data?._id,
          order: `#${data?.reference_id || "N/A"}`,
          date: new Date(data?.createdAt).toLocaleDateString(
            i18next.language === "ar" ? "ar-SA" : "en-US",
            { year: "numeric", month: "long", day: "2-digit" }
          ),
          status: data?.status?.toUpperCase() || "PENDING",
          total: data?.amountInCoins || 0,
          items: 1,
          product: data?.product || {},
          gift_voucher: data?.gift_voucher || [],
        };

        setOrderDetails(orderPayload);
        setShowSuccessModal(true);
      } else {
        dispatch(hideLoader());
        throw new Error(response.message || "Order placement failed");
      }
    } catch (error) {
      dispatch(hideLoader());
      console.log("Order placement error: ", error);
      if (error?.response?.data?.insufficientBalance) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.error?.message ||
          error?.message ||
          t("cart.order_failed") ||
          "Failed to place order";

        await Swal.fire({
          icon: "error",
          title: t("cart.error_title") || "Error",
          text: errorMessage,
          confirmButtonColor: "#d33",
          confirmButtonText: t("cart.ok_button") || "OK",
        });
      } else {
        showToast(
          error?.response?.data?.message ||
          error?.error?.message ||
          error?.message,
          "error"
        );

      }
    } finally {
      dispatch(hideLoader());
      fetchUserData();
    }
  }, [selectedProduct, neededCoins, dispatch, t]);

  // API functions
  const fetchUserData = useCallback(async () => {
    dispatch(showLoader());
    try {
      const response = await getApiRequest(END_POINTS.GET_USER_DATA);
      console.log("response:for user", response);
      if (response.success) {
        dispatch(setUser(response.user));
        setLocalStorageData("user", JSON.stringify(response.user));
      }
    } catch (error) {
      showToast(
        error?.response?.data?.message || error?.error?.message,
        "error"
      );
    } finally {
      dispatch(hideLoader());
    }
  }, [dispatch]);

  // Handle manual redirect
  const handleManualRedirect = useCallback(() => {
    navigate("/order-details", { state: { order: orderDetails } });
  }, [navigate, orderDetails]);

  // Memoized style objects
  const rtlStyles = useMemo(
    () => ({
      textAlign: isRTL ? "right" : "left",
      flexDirection: isRTL ? "row" : "row",
      marginRight: isRTL ? "0" : "10px",
      marginLeft: isRTL ? "10px" : "0",
    }),
    [isRTL]
  );

  const insufficientBoxStyles = useMemo(
    () => ({
      opacity: neededCoins > 0 ? 0.6 : 1,
      pointerEvents: neededCoins > 0 ? "none" : "auto",
      transition: "opacity 0.3s ease",
    }),
    [neededCoins]
  );

  const buyButtonStyles = useMemo(
    () => ({
      width: "100%",
      opacity: neededCoins > 0 || !selectedProduct ? 0.6 : 1,
      cursor: neededCoins > 0 || !selectedProduct ? "not-allowed" : "pointer",
    }),
    [neededCoins, selectedProduct]
  );

  // Memoized collapse items using the new items API
  const collapseItems = useMemo(
    () => [
      {
        key: "1",
        label: t("cart.disclaimer_title"),
        children: (
          <p className="overview-item-text">{t("cart.disclaimer_text")}</p>
        ),
      },
      {
        key: "2",
        label: t("cart.about_product_title"),
        children: (
          <p className="overview-item-text">{t("cart.about_product_text")}</p>
        ),
      },
      {
        key: "3",
        label: t("cart.repeat_policies_title"),
        children: (
          <p className="overview-item-text">{t("cart.repeat_policies_text")}</p>
        ),
      },
    ],
    [t]
  );

  const cartHeader = useMemo(
    () => (
      <div className="cart-header">
        <div style={rtlStyles}>
          <h3>{t("cart.title")}</h3>
          <p>{t("cart.subtitle")}</p>
        </div>
      </div>
    ),
    [t, rtlStyles]
  );

  // FIXED: Overview items with proper calculations
  const overviewItems = useMemo(
    () => [
      {
        key: "selected_items",
        value: selectedProduct ? "1" : "0",
      },
      ...(selectedProduct
        ? [
          {
            key: "selected_product",
            value: selectedProduct.product?.name || "N/A",
            customStyle: { fontSize: "12px" },
          },
        ]
        : []),
      {
        key: "total_cart_coin",
        value: (
          <>
            <img src={coin} alt="coin" className="market-place-coin" />{" "}
            {totalCoinsForSelected?.toLocaleString()}
          </>
        ),
      },
      {
        key: "coin_after_purchase",
        value: (
          <>
            <img src={coin} alt="coin" className="market-place-coin" />{" "}
            {Math.max(userCoins - totalCoinsForSelected, 0)?.toLocaleString()}
          </>
        ),
      },
    ],
    [selectedProduct, totalCoinsForSelected, userCoins]
  );

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <>
      <section className="cart-page-section">
        <div className="container">
          <div className="cart-wrapper">
            <Row gutter={25}>
              <Col
                xs={24}
                sm={24}
                md={24}
                lg={cartItems.length === 0 ? 24 : 17}
                xl={cartItems.length === 0 ? 24 : 17}
              >
                <Card className="cart-box">
                  {cartHeader}
                  <div className="cart-list">
                    {cartItems.length === 0 ? (
                      <p className="emty-message">{t("cart.empty_message")}</p>
                    ) : (
                      cartItems.map((item) => (
                        <MemoizedCartItem
                          key={item._id}
                          item={item}
                          isRTL={isRTL}
                          rtlStyles={rtlStyles}
                          selectedProduct={selectedProduct}
                          onCheckboxChange={handleCheckboxChange}
                          onRemoveProduct={handleRemoveProduct}
                          t={t}
                        />
                      ))
                    )}
                  </div>
                </Card>
              </Col>

              {cartItems.length > 0 && (
                <Col xs={24} sm={24} md={24} lg={7} xl={7}>
                  <Card className="overview-box">
                    {overviewItems.map((overviewItem, index) => (
                      <div
                        key={index}
                        className="overview-item"
                        style={rtlStyles}
                      >
                        <span className="overview-item-text">
                          {t(`cart.${overviewItem.key}`)}
                        </span>
                        <strong style={overviewItem.customStyle}>
                          {overviewItem.value}
                        </strong>
                      </div>
                    ))}

                    <MemoizedInsufficientBox
                      neededCoins={neededCoins}
                      selectedProduct={selectedProduct}
                      styles={insufficientBoxStyles}
                      buyButtonStyles={buyButtonStyles}
                      onBuyNow={handleBuyNow}
                      t={t}
                    />

                    <Collapse
                      ghost
                      items={collapseItems}
                      className="custom-collapse overview-box-collapse"
                    />
                  </Card>
                </Col>
              )}
            </Row>
          </div>
        </div>
      </section>

      {/* Success Modal */}
      <Modal
        open={showSuccessModal}
        onCancel={handleManualRedirect}
        footer={[
          <Button
            key="goNow"
            className="go-to-btn"
            type="primary"
            onClick={handleManualRedirect}
          >
            {t("cart.go_to_products_now") || "Go to Products Now"}
          </Button>,
        ]}
        centered
        closable={false}
        className="order-success-modal"
        maskClosable={false}
      >
        <Result
          icon={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
          title={t("cart.redeem_success_title") || "SUCCESSFULLY REDEEMED"}
          subTitle={
            t("cart.redeem_redirect_message").replace(
              "{{seconds}}",
              redirectCountdown
            ) ||
            `Redirecting to order details page in ${redirectCountdown} seconds...`
          }
        />
      </Modal>
    </>
  );
};

export default CartPage;
